import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Divider, LinearProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faChevronDown, faList } from '@fortawesome/pro-light-svg-icons';
import { GetNewsItemStatisticsResponse, NewsCitizenModel } from '../../../../api-service/clients';
import { TypographySettings } from '../../../../components/config';
import { FormDialog } from '../../../../components/form-dialog';
import { CopyToClipboard } from '../../../../components/copy-to-clipboard/copy-to-clipboard';
import { formatDate } from '../../../../utilities/date-helper';

const unknownDate = 'Ukendt dato';

const useStyles = makeStyles()(theme => ({
  barHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  citizenList: {
    maxHeight: 240,
    overflowY: 'auto',
  },
  dateHeading: {
    fontWeight: TypographySettings.DefaultFontSemiBold,
    marginTop: theme.spacing(1),
  },
  citizenActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}));

export interface INewsStatisticsDialogComponentProps {
  title?: string;
  statistics?: GetNewsItemStatisticsResponse;
  hasFailed: boolean;
  onClose: () => void;
}

export function NewsStatisticsDialogComponent(props: INewsStatisticsDialogComponentProps) {
  const { title, statistics, hasFailed, onClose } = props;
  const { classes } = useStyles();
  const receivedBy = statistics?.receivedBy ?? 0;

  return (
    <FormDialog
      open
      title={
        <>
          {title}
          <Typography variant="body2">Nyhedsstatistik</Typography>
        </>
      }
      confirmButtonText="Luk"
      onConfirmClick={onClose}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      {hasFailed && <Alert severity="error">Der opstod en fejl ved hentning af data. Prøv igen senere.</Alert>}
      {!hasFailed && !statistics && <Typography>Indlæser...</Typography>}
      {statistics && (
        <>
          <Box component="section">
            <NewsStatisticsBar label="Modtaget af" count={receivedBy} total={receivedBy} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box component="section">
            <NewsStatisticsBar label="Læst af" count={statistics.readBy} total={receivedBy} />
            <NewsCitizens citizens={statistics.readers ?? []} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box component="section">
            <NewsStatisticsBar label="Vist interesse" count={statistics.interestBy} total={receivedBy} />
            <NewsCitizens citizens={statistics.interested ?? []} />
          </Box>
        </>
      )}
    </FormDialog>
  );
}

interface INewsStatisticsBarProps {
  label: string;
  count: number;
  total: number;
}

function NewsStatisticsBar(props: INewsStatisticsBarProps) {
  const { label, count, total } = props;
  const { classes } = useStyles();
  const percentage = total === 0 ? 0 : Math.round((count / total) * 100);

  return (
    <Box>
      <Box className={classes.barHeader}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2">
          {count} / {total} ({percentage}%)
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={percentage} aria-label={label} />
    </Box>
  );
}

function NewsCitizens(props: { citizens: NewsCitizenModel[] }) {
  const { citizens } = props;
  const { classes } = useStyles();
  const [isGroupedByDate, setIsGroupedByDate] = useState(false);

  return (
    <Accordion disableGutters elevation={0} sx={{ mt: 1, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
        <Typography variant="body2">Vis borgere</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box className={classes.citizenList}>
          {isGroupedByDate
            ? groupByDate(citizens).map(group => (
                <Box key={group.date}>
                  <Typography variant="body2" className={classes.dateHeading}>
                    {group.date} ({group.citizens.length} borgere)
                  </Typography>
                  {group.citizens.map(citizen => (
                    <Typography key={citizen.citizenId} variant="body2" sx={{ pl: 1 }}>
                      {citizen.name} ({citizen.cpr})
                    </Typography>
                  ))}
                </Box>
              ))
            : citizens.map(citizen => (
                <Typography key={citizen.citizenId} variant="body2">
                  {citizen.name} ({citizen.cpr}) {formatDate(citizen.date)}
                </Typography>
              ))}
        </Box>
        <Box className={classes.citizenActions}>
          <ToggleButtonGroup value={isGroupedByDate ? 'grouped' : 'list'} exclusive size="small" onChange={(_, value) => value && setIsGroupedByDate(value === 'grouped')}>
            <ToggleButton value="list" title="Vis som liste" sx={{ gap: 0.5 }}>
              <FontAwesomeIcon icon={faList} />
              Liste
            </ToggleButton>
            <ToggleButton value="grouped" title="Gruppér efter dato" sx={{ gap: 0.5 }}>
              <FontAwesomeIcon icon={faCalendarDays} />
              Gruppér
            </ToggleButton>
          </ToggleButtonGroup>
          {citizens.length > 0 && (
            <CopyToClipboard toCopy={formatForClipboard(citizens, isGroupedByDate)} title="Kopiér borgere" toastText="Borgerne er kopieret til udklipsholderen" />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function groupByDate(citizens: NewsCitizenModel[]) {
  const byDate = new Map<string, NewsCitizenModel[]>();
  sortNewestFirst(citizens).forEach(citizen => {
    const date = formatDate(citizen.date, undefined, unknownDate);
    byDate.set(date, [...(byDate.get(date) ?? []), citizen]);
  });

  return Array.from(byDate, ([date, citizensOnDate]) => ({ date, citizens: sortByName(citizensOnDate) }));
}

function formatForClipboard(citizens: NewsCitizenModel[], isGroupedByDate: boolean) {
  if (!isGroupedByDate) {
    return citizens.map(citizen => `${citizen.name}\t${citizen.cpr}`).join('\n');
  }

  return groupByDate(citizens)
    .flatMap(group => group.citizens.map(citizen => `${citizen.name}\t${citizen.cpr}\t${group.date}`))
    .join('\n');
}

function sortNewestFirst(citizens: NewsCitizenModel[]) {
  return [...citizens].sort((a, b) => timeOf(b.date) - timeOf(a.date));
}

function sortByName(citizens: NewsCitizenModel[]) {
  return [...citizens].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
}

function timeOf(date: Date | string | undefined) {
  return date ? new Date(date).getTime() : 0;
}
